/**
 * Pure helpers for reconciling `Set-Cookie` response headers into a Playwright
 * storage-state cookie array.
 *
 * Used by {@link DirectPlaywrightClient}'s native api_sequence request session:
 * when an API step's response rotates auth cookies (e.g. an org `switch` that
 * re-issues `access_token`/`refresh_token`), we merge the new cookies into the
 * in-memory storage state so subsequent steps authenticate with the rotated
 * session. Kept Playwright-free so it can be unit-tested in isolation.
 *
 * @module set-cookie-merge
 */

/**
 * Playwright cookie shape (matches `BrowserContext.storageState().cookies[n]`
 * and the `storageState` accepted by `request.newContext`). `expires` is Unix
 * seconds, or `-1` for a session cookie.
 */
export interface PwCookie {
  name: string;
  value: string;
  domain: string;
  path: string;
  expires: number;
  httpOnly: boolean;
  secure: boolean;
  sameSite: 'Strict' | 'Lax' | 'None';
}

function normalizeSameSite(raw: string | undefined): PwCookie['sameSite'] {
  switch ((raw ?? '').trim().toLowerCase()) {
    case 'strict':
      return 'Strict';
    case 'none':
      return 'None';
    case 'lax':
    default:
      return 'Lax';
  }
}

/** Host of a request URL, used as the default (host-only) cookie domain. */
function hostFromUrl(requestUrl: string): string | null {
  try {
    return new URL(requestUrl).hostname || null;
  } catch {
    return null;
  }
}

/**
 * Parse one `Set-Cookie` header value into a {@link PwCookie}.
 *
 * Returns `null` when the header has no parseable `name=value` pair. A cookie
 * whose `Max-Age`/`Expires` is in the past is returned with a past `expires` so
 * {@link mergeCookies} can treat it as a deletion.
 *
 * @param header     a single `Set-Cookie` value (not a newline-joined list)
 * @param requestUrl the request URL, used to derive the domain when the cookie
 *                   omits a `Domain` attribute
 * @param nowMs      current time in ms (injectable for tests; defaults to `Date.now()`)
 */
export function parseSetCookie(
  header: string,
  requestUrl: string,
  nowMs: number = Date.now()
): PwCookie | null {
  const parts = header.split(';');
  const first = parts.shift();
  if (!first) return null;

  const eq = first.indexOf('=');
  if (eq <= 0) return null;
  const name = first.slice(0, eq).trim();
  const value = first.slice(eq + 1).trim();
  if (!name) return null;

  let domain: string | undefined;
  let path = '/';
  let expires = -1; // session cookie by default
  let httpOnly = false;
  let secure = false;
  let sameSite: string | undefined;
  let maxAge: number | undefined;
  let expiresAttr: string | undefined;

  for (const attr of parts) {
    const trimmed = attr.trim();
    if (!trimmed) continue;
    const aeq = trimmed.indexOf('=');
    const key = (aeq === -1 ? trimmed : trimmed.slice(0, aeq)).trim().toLowerCase();
    const val = aeq === -1 ? '' : trimmed.slice(aeq + 1).trim();
    switch (key) {
      case 'domain':
        domain = val.replace(/^\./, '') || undefined;
        break;
      case 'path':
        if (val) path = val;
        break;
      case 'max-age':
        maxAge = Number(val);
        break;
      case 'expires':
        expiresAttr = val;
        break;
      case 'httponly':
        httpOnly = true;
        break;
      case 'secure':
        secure = true;
        break;
      case 'samesite':
        sameSite = val;
        break;
    }
  }

  // Max-Age wins over Expires per RFC 6265. Both produce Unix seconds.
  if (maxAge !== undefined && Number.isFinite(maxAge)) {
    expires = Math.floor(nowMs / 1000) + maxAge;
  } else if (expiresAttr) {
    const parsed = Date.parse(expiresAttr);
    if (!Number.isNaN(parsed)) expires = Math.floor(parsed / 1000);
  }

  const requestHost = hostFromUrl(requestUrl)?.toLowerCase();
  if (!requestHost) return null;

  const resolvedDomain = (domain ?? requestHost).toLowerCase();

  // RFC 6265 §5.3: the Domain attribute must be the request host or a parent
  // suffix of it. Reject anything else — a response must not be able to scope a
  // cookie onto an unrelated/parent domain, which the shared isolated request
  // context would then attach to requests to other hosts (cross-host auth leak).
  const domainOk =
    resolvedDomain === requestHost || requestHost.endsWith(`.${resolvedDomain}`);
  if (!domainOk) return null;

  const resolvedSameSite = normalizeSameSite(sameSite);
  // Playwright's storageState validation rejects SameSite=None without Secure.
  // Coerce so a rotated cross-site cookie can't make the next newContext throw.
  const resolvedSecure = resolvedSameSite === 'None' ? true : secure;

  return {
    name,
    value,
    domain: resolvedDomain,
    path,
    expires,
    httpOnly,
    secure: resolvedSecure,
    sameSite: resolvedSameSite,
  };
}

/** Identity key for a cookie — name is scoped by (domain, path) per RFC 6265. */
function cookieKey(c: Pick<PwCookie, 'name' | 'domain' | 'path'>): string {
  const domain = c.domain.replace(/^\./, '').toLowerCase();
  return JSON.stringify([c.name, domain, c.path]);
}

/**
 * Upsert `incoming` cookies into `existing` by `(name, domain, path)`.
 *
 * An incoming cookie whose `expires` is in the past is treated as a deletion:
 * its matching entry (if any) is removed and the cookie is not added. Returns a
 * new array; inputs are not mutated.
 *
 * @param nowMs current time in ms (injectable for tests; defaults to `Date.now()`)
 */
export function mergeCookies(
  existing: readonly PwCookie[],
  incoming: readonly PwCookie[],
  nowMs: number = Date.now()
): PwCookie[] {
  const byKey = new Map<string, PwCookie>();
  for (const c of existing) byKey.set(cookieKey(c), c);

  const nowSec = Math.floor(nowMs / 1000);
  for (const c of incoming) {
    const key = cookieKey(c);
    const isExpired = c.expires > 0 && c.expires <= nowSec;
    if (isExpired) {
      byKey.delete(key);
    } else {
      byKey.set(key, c);
    }
  }

  return [...byKey.values()];
}

/**
 * Extract every `set-cookie` value from a Playwright `headersArray()` result.
 *
 * `APIResponse.headers()` collapses duplicate `set-cookie` into a single
 * newline-joined string, so callers must use `headersArray()` to recover the
 * individual headers. Header names are matched case-insensitively.
 */
export function extractSetCookieHeaders(
  headersArray: ReadonlyArray<{ name: string; value: string }>
): string[] {
  return headersArray
    .filter((h) => h.name.toLowerCase() === 'set-cookie')
    .map((h) => h.value);
}
